import React, { useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { shareService } from '../../services/shareService';
import { formatLocalDate } from '../../utils/dateUtils';
import {
  NPC_FAQ_URL,
  STATDOG_COPYRIGHT,
  STATDOG_CTA_LABEL,
  STATDOG_SITE_URL,
  classifySheetModel,
  computeGoalsPlusMinus,
  computeNpc,
  formatClassYear,
  formatPosition,
  formatSigned,
  isRegularSpringSeason,
  perGame,
  seasonYear,
} from '../../utils/playerSheetStats';
import { toast } from 'react-toastify';
import { PlayerSheetData, PlayerSheetVariant, SeasonHistoryRow, SeasonStats } from './playerSheetTypes';

type SheetTab = 'season' | 'games' | 'career';

interface PlayerStatSheetViewProps {
  data: PlayerSheetData;
  variant?: PlayerSheetVariant;
  readOnly?: boolean;
  clientId?: string | null;
  initialSeasonId?: string;
}

const npcOf = (stats?: SeasonStats | null) =>
  stats?.npc ?? computeNpc(stats);

const gPlusOf = (stats?: SeasonStats | null) =>
  stats?.goalsPlusMinus ?? computeGoalsPlusMinus(npcOf(stats));

const NpcLink = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <a
    href={NPC_FAQ_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={className || 'underline decoration-dotted underline-offset-2 hover:text-[#0a5490]'}
    title="What is NPC? See Metrics and Data"
  >
    {children}
  </a>
);

const StatLine = ({
  label,
  value,
  href,
}: {
  label: string;
  value: React.ReactNode;
  href?: string;
}) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-[#e6eef5] last:border-0">
    <span className="text-sm text-gray-600">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted underline-offset-2 hover:text-[#0a5490]"
        >
          {label}
        </a>
      ) : (
        label
      )}
    </span>
    <span className="text-sm font-semibold text-[#19133c] text-right">{value}</span>
  </div>
);

const MetricCard = ({
  label,
  value,
  sub,
  href,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  href?: string;
}) => (
  <div className="border border-[#bccedf] bg-white rounded-md px-3 py-2.5 text-center">
    <div className="text-xl sm:text-2xl font-bold text-[#05396b]">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mt-0.5">
      {href ? <NpcLink className="underline decoration-dotted underline-offset-2 hover:text-[#0a5490]">{label}</NpcLink> : label}
    </div>
    {sub ? <div className="text-xs text-gray-500 italic mt-0.5">{sub}</div> : null}
  </div>
);

const sumCareerRows = (rows: SeasonHistoryRow[]): SeasonStats => {
  const totals = rows.reduce<SeasonStats>(
    (acc, row) => {
      (
        [
          'gamesPlayed',
          'shots',
          'shotsaved',
          'shotsongoal',
          'goals',
          'assist',
          'groundballs',
          'penalties',
          'turnovers',
          'causedturnover',
          'faceoffwins',
          'faceofflosses',
          'dcTaken',
          'dcEarned',
          'foulShootingSpace',
          'foulThreeSecond',
          'foulDangerousCheck',
          'yellowCards',
          'redCards',
          'eightMeterAttempts',
          'eightMeterGoals',
          'eightMeterSaves',
          'eightMeterGoalsAllowed',
          'saved',
          'goalsallowed',
          'blocks',
          'sogTaken',
          'points',
        ] as const
      ).forEach((field) => {
        acc[field] = (Number(acc[field]) || 0) + (Number(row[field]) || 0);
      });
      return acc;
    },
    {},
  );
  const npc = npcOf(totals);
  const shots = totals.shots || 0;
  const goals = totals.goals || 0;
  const shotsaved = totals.shotsaved || 0;
  const sogTaken = totals.sogTaken ?? goals + shotsaved;
  const foTotal = (totals.faceoffwins || 0) + (totals.faceofflosses || 0);
  const shotsFaced = totals.shotsongoal || 0;
  return {
    ...totals,
    points: goals + (totals.assist || 0),
    sogTaken,
    shotPct: shots > 0 ? Math.round((goals / shots) * 1000) / 10 : 0,
    sogPct: shots > 0 ? Math.round((sogTaken / shots) * 1000) / 10 : 0,
    faceoffPct: foTotal > 0 ? Math.round(((totals.faceoffwins || 0) / foTotal) * 1000) / 10 : 0,
    savePct: shotsFaced > 0 ? Math.round(((totals.saved || 0) / shotsFaced) * 1000) / 10 : 0,
    npc,
    goalsPlusMinus: computeGoalsPlusMinus(npc),
  };
};

const PlayerStatSheetView: React.FC<PlayerStatSheetViewProps> = ({
  data,
  variant = 'season',
  readOnly = false,
  clientId,
  initialSeasonId,
}) => {
  const [tab, setTab] = useState<SheetTab>('season');
  const seasonHistory = useMemo(() => data.seasonHistory || [], [data.seasonHistory]);
  const careerSeasons = useMemo(() => {
    const regular = seasonHistory.filter((row) => isRegularSpringSeason(row));
    const current =
      seasonHistory.find((row) => row.isCurrent) ||
      seasonHistory.find((row) => row.seasonId && row.seasonId === data.season?._id);
    const merged = [...regular];
    if (current && !merged.some((row) => row.seasonId === current.seasonId)) {
      merged.unshift(current);
    }
    if (merged.length === 0 && (data.season?._id || data.seasonStats)) {
      merged.push({
        ...data.seasonStats,
        seasonId: data.season?._id || 'current',
        seasonName: data.season?.name || 'Season',
        startDate: data.season?.startDate || null,
        isCurrent: true,
        isRegularSeason: true,
      });
    }
    return merged.sort((a, b) => Number(seasonYear(b) || 0) - Number(seasonYear(a) || 0));
  }, [seasonHistory, data.season, data.seasonStats]);
  const defaultSeasonId =
    initialSeasonId ||
    data.season?._id ||
    seasonHistory.find((row) => row.isCurrent)?.seasonId ||
    seasonHistory[0]?.seasonId ||
    '';
  const [selectedSeasonId, setSelectedSeasonId] = useState(defaultSeasonId);

  useEffect(() => {
    setSelectedSeasonId(defaultSeasonId);
  }, [defaultSeasonId]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sharingLink, setSharingLink] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copyingLink, setCopyingLink] = useState(false);

  const canShare = !readOnly && sessionManager.canAccessDataSharing();
  const playerName = `${data.player.firstName || ''} ${data.player.lastName || ''}`.trim() || 'Player';
  const teamName = data.client?.name || sessionManager.getClientName() || 'Team';
  const selectedHistory = seasonHistory.find((row) => row.seasonId === selectedSeasonId);
  const selectedGames = useMemo(() => {
    const fromHistory = data.gamesBySeason?.find((row) => row.seasonId === selectedSeasonId)?.games;
    if (fromHistory) return fromHistory;
    if (selectedSeasonId && selectedSeasonId === data.season?._id) return data.gameBreakdown || [];
    return data.gameBreakdown || [];
  }, [data.gamesBySeason, data.gameBreakdown, data.season?._id, selectedSeasonId]);
  const seasonName = selectedHistory?.seasonName || data.season?.name || sessionManager.getSeasonName() || 'Season';
  const isGoalie = !!(data.player.isGoalie || data.seasonStats.isGoalie);
  const model = classifySheetModel(data.player.position, isGoalie);
  const s = selectedHistory || data.seasonStats;
  const playedGames = useMemo(
    () => selectedGames.filter((g) => g.played),
    [selectedGames],
  );
  const gp = playedGames.length || s.gamesPlayed || 0;
  const npc = npcOf(s);
  const gPlus = gPlusOf(s);
  const missed = s.shotmissed ?? Math.max((s.shots || 0) - (s.sogTaken || 0), 0);
  const career = useMemo(
    () => (careerSeasons.length > 0 ? sumCareerRows(careerSeasons) : data.careerStats),
    [careerSeasons, data.careerStats],
  );
  const drawOf = (st?: SeasonStats | null) => {
    const earned = Number(st?.dcEarned) || 0;
    const taken =
      Number(st?.dcTaken) || (Number(st?.faceoffwins) || 0) + (Number(st?.faceofflosses) || 0);
    const pct = taken > 0 ? Math.round((earned / taken) * 1000) / 10 : 0;
    return { earned, taken, pct };
  };
  const foulCount = (st?: SeasonStats | null) =>
    (Number(st?.foulShootingSpace) || 0) +
    (Number(st?.foulThreeSecond) || 0) +
    (Number(st?.foulDangerousCheck) || 0);
  const draws = drawOf(s);
  const careerDraws = drawOf(career);
  const showFaceoffCard = model === 'faceoff' || draws.taken > 0 || draws.earned > 0;
  const showFaceoffColumns =
    model === 'faceoff' || showFaceoffCard || careerDraws.taken > 0 || careerDraws.earned > 0;
  const logoUrl = data.client?.logo
    ? `${process.env.REACT_APP_UPLOAD_URL}uploads/clients/${data.client.logo}`
    : '';
  const classYear = formatClassYear(data.player.graduation);
  const positionLabel = formatPosition(data.player.position);
  const gameRows = useMemo(
    () => playedGames.filter((g) => g.stats),
    [playedGames],
  );
  const showGoalieGoals =
    model === 'goalie' &&
    ((s.goals ?? 0) > 0 || gameRows.some((g) => (g.stats?.goals ?? 0) > 0));
  const gameColSpan =
    model === 'goalie'
      ? showGoalieGoals
        ? 11
        : 10
      : model === 'faceoff'
        ? 12
        : model === 'defense'
          ? showFaceoffColumns
            ? 15
            : 12
          : showFaceoffColumns
            ? 15
            : 12;
  const careerColSpan =
    model === 'goalie' ? 11 : model === 'faceoff' ? 12 : showFaceoffColumns ? 14 : 11;

  const primaryMetrics =
    model === 'goalie'
      ? [
          { label: 'Saves (Sv)', value: s.saved ?? 0, sub: `${perGame(s.saved, gp)} SVPG` },
          { label: 'Goals Allowed', value: s.goalsallowed ?? 0, sub: `${perGame(s.goalsallowed, gp)} GAPG` },
          { label: 'Save %', value: `${s.savePct ?? 0}%` },
          { label: '8M Saves', value: s.eightMeterSaves ?? 0 },
          { label: '8M GA', value: s.eightMeterGoalsAllowed ?? 0 },
        ]
      : model === 'faceoff'
        ? [
            { label: 'Draw Controls (DC)', value: draws.earned },
            { label: 'Draw Percentage (DP)', value: `${draws.pct}%` },
            { label: 'Total Draws (TD)', value: draws.taken },
            { label: 'Points (P)', value: s.points ?? 0, sub: `${perGame(s.points, gp)} PPG` },
            { label: 'Goals (G)', value: s.goals ?? 0, sub: `${perGame(s.goals, gp)} GPG` },
          ]
        : model === 'defense'
          ? [
              { label: 'GB', value: s.groundballs ?? 0, sub: `${perGame(s.groundballs, gp)} GBPG` },
              { label: 'CTO', value: s.causedturnover ?? 0, sub: `${perGame(s.causedturnover, gp)} CTOPG` },
              { label: 'Blocks', value: s.blocks ?? 0 },
              { label: 'Fouls', value: foulCount(s) },
              { label: 'Points (P)', value: s.points ?? 0 },
            ]
          : [
              { label: 'Points (P)', value: s.points ?? 0, sub: `${perGame(s.points, gp)} PPG` },
              { label: 'Goals (G)', value: s.goals ?? 0, sub: `${perGame(s.goals, gp)} GPG` },
              { label: 'Assists (A)', value: s.assist ?? 0, sub: `${perGame(s.assist, gp)} APG` },
              { label: 'Shots (S)', value: s.shots ?? 0 },
              { label: 'Shooting %', value: `${s.shotPct ?? 0}%` },
            ];

  const handleDownloadPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.post(
        API_URLS.EXPORT_DOWNLOAD_PLAYER_SHEET,
        {
          player: data.player,
          seasonStats: { ...s, npc, goalsPlusMinus: gPlus },
          gameBreakdown: data.gameBreakdown,
          teamName,
          seasonName: variant === 'career' ? '' : seasonName,
          clientId: clientId || data.client?._id,
          clientLogo: data.client?.logo,
          seasonHistory: careerSeasons,
        },
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${playerName.replace(/\s+/g, '-')}-Stat-Sheet.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to download PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleShareLink = async () => {
    const shareSeasonId = data.season?._id;
    const sharePlayerSeasonId = data.player.playerSeasonId;
    if (!shareSeasonId || !sharePlayerSeasonId) return;
    setSharingLink(true);
    try {
      const result = await shareService.generateShareLink('season', shareSeasonId, 'player', sharePlayerSeasonId);
      setShareUrl(result.shareUrl);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create share link');
    } finally {
      setSharingLink(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareUrl) return;
    setCopyingLink(true);
    try {
      const copied = await shareService.copyToClipboard(shareUrl);
      toast[copied ? 'success' : 'error'](copied ? 'Link copied to clipboard' : 'Failed to copy link');
    } finally {
      setCopyingLink(false);
    }
  };

  const tabs: { id: SheetTab; label: string }[] = [
    { id: 'season', label: 'Current Season' },
    { id: 'games', label: 'Game Log' },
    { id: 'career', label: 'Career / Multi-Season' },
  ];

  const seasonLabel = (row: SeasonHistoryRow) => seasonYear(row) || row.seasonName || 'Season';

  return (
    <div className="flex flex-col gap-4">
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={exportingPdf}
            className="px-3 py-1.5 rounded-md bg-[#05396b] text-white text-sm font-semibold hover:bg-[#04305a] disabled:opacity-60"
          >
            {exportingPdf ? 'Exporting…' : 'Export PDF'}
          </button>
          {canShare && (
            <button
              type="button"
              onClick={handleShareLink}
              disabled={sharingLink}
              className="px-3 py-1.5 rounded-md border-2 border-[#05396b] text-[#05396b] text-sm font-semibold hover:bg-[#eef5fc] disabled:opacity-60"
            >
              {sharingLink ? 'Creating link…' : 'Public Link'}
            </button>
          )}
        </div>
      )}

      {shareUrl && !readOnly && (
        <div className="p-3 bg-[#eef5fc] border border-[#bccedf] rounded-md text-sm flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="min-w-0 flex-1 break-all">
            <span className="font-semibold text-[#05396b]">Share link: </span>
            <a href={shareUrl} target="_blank" rel="noreferrer" className="text-[#05396b] underline">
              {shareUrl}
            </a>
          </div>
          <button
            type="button"
            onClick={handleCopyShareLink}
            disabled={copyingLink}
            className="shrink-0 px-2 py-1 text-[#05396b] font-semibold"
          >
            Copy
          </button>
        </div>
      )}

      {variant === 'career' && seasonHistory.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label htmlFor="player-sheet-season" className="text-sm font-semibold text-[#05396b]">
            Season
          </label>
          <select
            id="player-sheet-season"
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
            className="w-full sm:w-auto min-w-[220px] px-3 py-2 text-sm rounded-md border border-[#bccedf] bg-white text-[#05396b] font-medium focus:outline-none"
          >
            {seasonHistory.map((row) => (
              <option key={row.seasonId} value={row.seasonId}>
                {seasonLabel(row)}
              </option>
            ))}
          </select>
        </div>
      )}

      <section className="border border-[#bccedf] rounded-lg overflow-hidden bg-white">
        <div className="bg-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="bg-white rounded-md px-1 py-1">
            <img src="/images/logo.png" alt="STATDOG" className="h-9 w-auto object-contain" />
          </div>
          {logoUrl ? (
            <div className="h-12 w-12 shrink-0 bg-white rounded-md border border-[#e6eef5] p-1 flex items-center justify-center">
              <img src={logoUrl} alt={teamName} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div className="text-[#05396b] text-xs font-semibold text-right">{teamName}</div>
          )}
        </div>
        <div className="h-1 bg-[#05396b]" aria-hidden="true" />
        <div className="px-4 py-3">
          <div className="text-lg sm:text-xl font-bold text-[#19133c]">
            #{data.player.no} {playerName}
          </div>
          <div className="text-sm text-[#05396b] font-medium mt-1">
            {[positionLabel, classYear, `${gp} GP`].filter(Boolean).join(' | ')}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 px-4 pb-4">
          {primaryMetrics.map((metric) => (
            <MetricCard key={metric.label} {...metric} />
          ))}
        </div>
      </section>

      <div className="flex border-b border-[#bccedf]">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`flex-1 px-2 py-2 text-xs sm:text-sm font-semibold ${
              tab === item.id
                ? 'text-[#05396b] border-b-2 border-[#05396b]'
                : 'text-gray-500 hover:text-[#05396b]'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'season' && (
        <div className="grid grid-cols-1 gap-3">
          {model === 'goalie' ? (
            <section className="border border-[#bccedf] rounded-lg p-4 bg-white">
              <h3 className="text-sm font-bold text-[#05396b] uppercase tracking-wide mb-2">Goalie</h3>
              <StatLine label="Saves (Sv)" value={s.saved ?? 0} />
              <StatLine label="Saves / Game (SVPG)" value={perGame(s.saved, gp)} />
              <StatLine label="Goals Allowed (GA)" value={s.goalsallowed ?? 0} />
              <StatLine label="GA / Game (GAPG)" value={perGame(s.goalsallowed, gp)} />
              <StatLine label="Save % (Sv%)" value={`${s.savePct ?? 0}%`} />
              <StatLine label="Shots on Goal (SOG)" value={s.shotsongoal ?? 0} />
              {(s.goals ?? 0) > 0 && <StatLine label="Goals (G)" value={s.goals ?? 0} />}
            </section>
          ) : (
            <section className="border border-[#bccedf] rounded-lg p-4 bg-white">
              <h3 className="text-sm font-bold text-[#05396b] uppercase tracking-wide mb-2">Scoring & Shooting</h3>
              <StatLine label="Shots (S)" value={s.shots ?? 0} />
              <StatLine label="Missed" value={missed} />
              <StatLine label="Goals (G)" value={s.goals ?? 0} />
              <StatLine label="Shooting % (S%)" value={`${s.shotPct ?? 0}%`} />
              <StatLine label="Assists (A)" value={s.assist ?? 0} />
              <StatLine label="Points (P)" value={s.points ?? 0} />
              <StatLine label="PPG" value={perGame(s.points, gp)} />
              <StatLine label="GPG" value={perGame(s.goals, gp)} />
              <StatLine label="APG" value={perGame(s.assist, gp)} />
            </section>
          )}

          {(s.eightMeterAttempts ?? 0) > 0 && (
            <section className="border border-[#bccedf] rounded-lg p-4 bg-white">
              <h3 className="text-sm font-bold text-[#05396b] uppercase tracking-wide mb-2">8-Meter</h3>
              <StatLine label="Attempts" value={s.eightMeterAttempts ?? 0} />
              <StatLine label="Goals" value={s.eightMeterGoals ?? 0} />
              <StatLine
                label="Conversion %"
                value={`${
                  s.eightMeterPct ??
                  ((s.eightMeterAttempts || 0) > 0
                    ? Math.round(((s.eightMeterGoals || 0) / (s.eightMeterAttempts || 1)) * 1000) / 10
                    : 0)
                }%`}
              />
            </section>
          )}

          {showFaceoffCard && (
            <section className="border border-[#bccedf] rounded-lg p-4 bg-white">
              <h3 className="text-sm font-bold text-[#05396b] uppercase tracking-wide mb-2">Draw Controls</h3>
              <StatLine label="Draw Controls (DC)" value={draws.earned} />
              <StatLine label="Draw Percentage (DP)" value={`${draws.pct}%`} />
              <StatLine label="Total Draws (TD)" value={draws.taken} />
            </section>
          )}

          <section className="border border-[#bccedf] rounded-lg p-4 bg-white">
            <h3 className="text-sm font-bold text-[#05396b] uppercase tracking-wide mb-2">Discipline & Grit</h3>
            <StatLine label="Fouls" value={foulCount(s)} />
            <StatLine label="Shooting Space" value={s.foulShootingSpace ?? 0} />
            <StatLine label="3 Second" value={s.foulThreeSecond ?? 0} />
            <StatLine label="Dangerous Check" value={s.foulDangerousCheck ?? 0} />
            <StatLine label="Yellow Cards" value={s.yellowCards ?? 0} />
            <StatLine label="Red Cards" value={s.redCards ?? 0} />
            <StatLine label="Shots Blocked" value={s.blocks ?? 0} />
          </section>
        </div>
      )}

      {tab === 'games' && (
        <div className="overflow-x-auto border border-[#bccedf] rounded-md">
          <table className="min-w-full text-xs sm:text-sm text-center">
            <thead>
              <tr className="bg-[#05396b] text-white">
                <th className="px-2 py-2 border border-[#bccedf]">Date</th>
                <th className="px-2 py-2 border border-[#bccedf] text-left">Opponent</th>
                {model === 'goalie' ? (
                  <>
                    <th className="px-2 py-2 border border-[#bccedf]">Sv</th>
                    <th className="px-2 py-2 border border-[#bccedf]">GA</th>
                    <th className="px-2 py-2 border border-[#bccedf]">Sv%</th>
                    <th className="px-2 py-2 border border-[#bccedf]">SOG</th>
                    {showGoalieGoals && <th className="px-2 py-2 border border-[#bccedf]">G</th>}
                  </>
                ) : model === 'faceoff' ? (
                  <>
                    <th className="px-2 py-2 border border-[#bccedf]">DC</th>
                    <th className="px-2 py-2 border border-[#bccedf]">DP</th>
                    <th className="px-2 py-2 border border-[#bccedf]">TD</th>
                    <th className="px-2 py-2 border border-[#bccedf]">G</th>
                    <th className="px-2 py-2 border border-[#bccedf]">A</th>
                    <th className="px-2 py-2 border border-[#bccedf]">P</th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2 border border-[#bccedf]">G</th>
                    <th className="px-2 py-2 border border-[#bccedf]">A</th>
                    <th className="px-2 py-2 border border-[#bccedf]">P</th>
                    <th className="px-2 py-2 border border-[#bccedf]">S (SOG)</th>
                    <th className="px-2 py-2 border border-[#bccedf]">S%</th>
                    {showFaceoffColumns && (
                      <>
                        <th className="px-2 py-2 border border-[#bccedf]">DC</th>
                        <th className="px-2 py-2 border border-[#bccedf]">DP</th>
                        <th className="px-2 py-2 border border-[#bccedf]">TD</th>
                      </>
                    )}
                  </>
                )}
                <th className="px-2 py-2 border border-[#bccedf]">GB</th>
                <th className="px-2 py-2 border border-[#bccedf]">TO</th>
                <th className="px-2 py-2 border border-[#bccedf]">CTO</th>
                {model === 'field' && <th className="px-2 py-2 border border-[#bccedf]">SS</th>}
                {model === 'defense' && <th className="px-2 py-2 border border-[#bccedf]">Blocks</th>}
                <th className="px-2 py-2 border border-[#bccedf]">
                  <NpcLink className="text-white underline decoration-dotted underline-offset-2">NPC</NpcLink>
                </th>
              </tr>
            </thead>
            <tbody>
              {gameRows.length === 0 ? (
                <tr>
                  <td colSpan={gameColSpan} className="py-8 text-gray-500 italic bg-white">
                    No game stats yet for this season.
                  </td>
                </tr>
              ) : (
                gameRows.map((g, idx) => {
                  const st = g.stats!;
                  const rowNpc = npcOf(st);
                  return (
                    <tr key={g.gameId} className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}>
                      <td className="px-2 py-1.5 border border-gray-300">
                        {g.date ? formatLocalDate(g.date, undefined, { month: 'numeric', day: 'numeric' }) : '-'}
                      </td>
                      <td className="px-2 py-1.5 border border-gray-300 text-left font-medium">{g.opponent}</td>
                      {model === 'goalie' ? (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{st.saved ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.goalsallowed ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.savePct ?? 0}%</td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.shotsongoal ?? 0}</td>
                          {showGoalieGoals && (
                            <td className="px-2 py-1.5 border border-gray-300">{st.goals ?? 0}</td>
                          )}
                        </>
                      ) : model === 'faceoff' ? (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(st).earned}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(st).pct}%</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(st).taken}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.goals ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.assist ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300 font-semibold">{st.points ?? 0}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{st.goals ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.assist ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300 font-semibold">{st.points ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">
                            {st.shots ?? 0} ({st.sogTaken ?? 0})
                          </td>
                          <td className="px-2 py-1.5 border border-gray-300">{st.shotPct ?? 0}%</td>
                          {showFaceoffColumns && (
                            <>
                              <td className="px-2 py-1.5 border border-gray-300">{drawOf(st).earned}</td>
                              <td className="px-2 py-1.5 border border-gray-300">{drawOf(st).pct}%</td>
                              <td className="px-2 py-1.5 border border-gray-300">{drawOf(st).taken}</td>
                            </>
                          )}
                        </>
                      )}
                      <td className="px-2 py-1.5 border border-gray-300">{st.groundballs ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{st.turnovers ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{st.causedturnover ?? 0}</td>
                      {model === 'field' && (
                        <td className="px-2 py-1.5 border border-gray-300">{st.shotsaved ?? 0}</td>
                      )}
                      {model === 'defense' && (
                        <td className="px-2 py-1.5 border border-gray-300">{st.blocks ?? 0}</td>
                      )}
                      <td className="px-2 py-1.5 border border-gray-300 font-semibold">{formatSigned(rowNpc)}</td>
                    </tr>
                  );
                })
              )}
              {gameRows.length > 0 && (
                <tr className="bg-[#eef5fc] font-bold">
                  <td className="px-2 py-1.5 border border-gray-300">TOTAL</td>
                  <td className="px-2 py-1.5 border border-gray-300 text-left">{gp} Games</td>
                  {model === 'goalie' ? (
                    <>
                      <td className="px-2 py-1.5 border border-gray-300">{s.saved ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.goalsallowed ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.savePct ?? 0}%</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.shotsongoal ?? 0}</td>
                      {showGoalieGoals && (
                        <td className="px-2 py-1.5 border border-gray-300">{s.goals ?? 0}</td>
                      )}
                    </>
                  ) : model === 'faceoff' ? (
                    <>
                      <td className="px-2 py-1.5 border border-gray-300">{drawOf(s).earned}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{drawOf(s).pct}%</td>
                      <td className="px-2 py-1.5 border border-gray-300">{drawOf(s).taken}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.goals ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.assist ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.points ?? 0}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-2 py-1.5 border border-gray-300">{s.goals ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.assist ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.points ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">
                        {s.shots ?? 0} ({s.sogTaken ?? 0})
                      </td>
                      <td className="px-2 py-1.5 border border-gray-300">{s.shotPct ?? 0}%</td>
                      {showFaceoffColumns && (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(s).earned}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(s).pct}%</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(s).taken}</td>
                        </>
                      )}
                    </>
                  )}
                  <td className="px-2 py-1.5 border border-gray-300">{s.groundballs ?? 0}</td>
                  <td className="px-2 py-1.5 border border-gray-300">{s.turnovers ?? 0}</td>
                  <td className="px-2 py-1.5 border border-gray-300">{s.causedturnover ?? 0}</td>
                  {model === 'field' && (
                    <td className="px-2 py-1.5 border border-gray-300">{s.shotsaved ?? 0}</td>
                  )}
                  {model === 'defense' && (
                    <td className="px-2 py-1.5 border border-gray-300">{s.blocks ?? 0}</td>
                  )}
                  <td className="px-2 py-1.5 border border-gray-300">{formatSigned(npc)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'career' && (
        <div className="overflow-x-auto border border-[#bccedf] rounded-md">
          <table className="min-w-full text-xs sm:text-sm text-center">
            <thead>
              <tr className="bg-[#05396b] text-white">
                <th className="px-2 py-2 border border-[#bccedf] text-left">Season</th>
                <th className="px-2 py-2 border border-[#bccedf]">Year</th>
                <th className="px-2 py-2 border border-[#bccedf]">GP</th>
                {model === 'goalie' ? (
                  <>
                    <th className="px-2 py-2 border border-[#bccedf]">Sv</th>
                    <th className="px-2 py-2 border border-[#bccedf]">GA</th>
                    <th className="px-2 py-2 border border-[#bccedf]">Sv%</th>
                    <th className="px-2 py-2 border border-[#bccedf]">SOG</th>
                  </>
                ) : model === 'faceoff' ? (
                  <>
                    <th className="px-2 py-2 border border-[#bccedf]">DC</th>
                    <th className="px-2 py-2 border border-[#bccedf]">DP</th>
                    <th className="px-2 py-2 border border-[#bccedf]">TD</th>
                    <th className="px-2 py-2 border border-[#bccedf]">G</th>
                    <th className="px-2 py-2 border border-[#bccedf]">A</th>
                    <th className="px-2 py-2 border border-[#bccedf]">P</th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2 border border-[#bccedf]">G</th>
                    <th className="px-2 py-2 border border-[#bccedf]">A</th>
                    <th className="px-2 py-2 border border-[#bccedf]">P</th>
                    <th className="px-2 py-2 border border-[#bccedf]">S%</th>
                    {showFaceoffColumns && (
                      <>
                        <th className="px-2 py-2 border border-[#bccedf]">DC</th>
                        <th className="px-2 py-2 border border-[#bccedf]">DP</th>
                        <th className="px-2 py-2 border border-[#bccedf]">TD</th>
                      </>
                    )}
                  </>
                )}
                <th className="px-2 py-2 border border-[#bccedf]">GB</th>
                <th className="px-2 py-2 border border-[#bccedf]">TO</th>
                <th className="px-2 py-2 border border-[#bccedf]">
                  <NpcLink className="text-white underline decoration-dotted underline-offset-2">NPC</NpcLink>
                </th>
                <th className="px-2 py-2 border border-[#bccedf]">G+/-</th>
              </tr>
            </thead>
            <tbody>
              {careerSeasons.length === 0 ? (
                <tr>
                  <td colSpan={careerColSpan} className="py-8 text-gray-500 italic bg-white">
                    No season history yet.
                  </td>
                </tr>
              ) : (
                careerSeasons.map((row, idx) => {
                  const rowNpc = npcOf(row);
                  return (
                    <tr
                      key={row.seasonId || idx}
                      className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}
                    >
                      <td className="px-2 py-1.5 border border-gray-300 text-left whitespace-nowrap">
                        {seasonLabel(row)}
                      </td>
                      <td className="px-2 py-1.5 border border-gray-300">{seasonYear(row)}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{row.gamesPlayed ?? 0}</td>
                      {model === 'goalie' ? (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{row.saved ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.goalsallowed ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.savePct ?? 0}%</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.shotsongoal ?? 0}</td>
                        </>
                      ) : model === 'faceoff' ? (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(row).earned}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(row).pct}%</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(row).taken}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.goals ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.assist ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.points ?? 0}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{row.goals ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.assist ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.points ?? 0}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{row.shotPct ?? 0}%</td>
                          {showFaceoffColumns && (
                            <>
                              <td className="px-2 py-1.5 border border-gray-300">{drawOf(row).earned}</td>
                              <td className="px-2 py-1.5 border border-gray-300">{drawOf(row).pct}%</td>
                              <td className="px-2 py-1.5 border border-gray-300">{drawOf(row).taken}</td>
                            </>
                          )}
                        </>
                      )}
                      <td className="px-2 py-1.5 border border-gray-300">{row.groundballs ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{row.turnovers ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{formatSigned(rowNpc)}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{formatSigned(gPlusOf(row), 2)}</td>
                    </tr>
                  );
                })
              )}
              {career && careerSeasons.length > 0 && (
                <tr className="bg-[#eef5fc] font-bold">
                  <td className="px-2 py-1.5 border border-gray-300 text-left">CAREER</td>
                  <td className="px-2 py-1.5 border border-gray-300">{careerSeasons.length} Yrs</td>
                  <td className="px-2 py-1.5 border border-gray-300">{career.gamesPlayed ?? 0}</td>
                  {model === 'goalie' ? (
                    <>
                      <td className="px-2 py-1.5 border border-gray-300">{career.saved ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.goalsallowed ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.savePct ?? 0}%</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.shotsongoal ?? 0}</td>
                    </>
                  ) : model === 'faceoff' ? (
                    <>
                      <td className="px-2 py-1.5 border border-gray-300">{drawOf(career).earned}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{drawOf(career).pct}%</td>
                      <td className="px-2 py-1.5 border border-gray-300">{drawOf(career).taken}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.goals ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.assist ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.points ?? 0}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-2 py-1.5 border border-gray-300">{career.goals ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.assist ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.points ?? 0}</td>
                      <td className="px-2 py-1.5 border border-gray-300">{career.shotPct ?? 0}%</td>
                      {showFaceoffColumns && (
                        <>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(career).earned}</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(career).pct}%</td>
                          <td className="px-2 py-1.5 border border-gray-300">{drawOf(career).taken}</td>
                        </>
                      )}
                    </>
                  )}
                  <td className="px-2 py-1.5 border border-gray-300">{career.groundballs ?? 0}</td>
                  <td className="px-2 py-1.5 border border-gray-300">{career.turnovers ?? 0}</td>
                  <td className="px-2 py-1.5 border border-gray-300">{formatSigned(npcOf(career))}</td>
                  <td className="px-2 py-1.5 border border-gray-300">{formatSigned(gPlusOf(career), 2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {readOnly && (
        <footer className="text-center pt-6 pb-2 border-t border-[#e6eef5] mt-4">
          <a
            href={STATDOG_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#7fe526] text-black text-sm font-semibold hover:bg-[#6fd315]"
          >
            {STATDOG_CTA_LABEL}
          </a>
          <p className="text-xs text-gray-400 mt-3">{STATDOG_COPYRIGHT}</p>
        </footer>
      )}
    </div>
  );
};

export default PlayerStatSheetView;
